const BASE = '/api/auth';
const ACCESS_KEY  = 'trackapp:access_token';
const REFRESH_KEY = 'trackapp:refresh_token';
const ADMIN_ACCESS_BACKUP_KEY = 'trackapp:admin_access_token';
const ADMIN_REFRESH_BACKUP_KEY = 'trackapp:admin_refresh_token';

export const REFRESHED = 'refreshed';
export const IDENTITY_RESTORED = 'identity_restored';
export const REFRESH_FAILED = 'failed';

export function getAccessToken()  { return localStorage.getItem(ACCESS_KEY);  }
export function getRefreshToken() { return localStorage.getItem(REFRESH_KEY); }

export function setTokens(access, refresh) {
  localStorage.setItem(ACCESS_KEY,  access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ADMIN_ACCESS_BACKUP_KEY);
  localStorage.removeItem(ADMIN_REFRESH_BACKUP_KEY);
}

export function hasAdminTokenBackup() {
  return !!localStorage.getItem(ADMIN_ACCESS_BACKUP_KEY)
    && !!localStorage.getItem(ADMIN_REFRESH_BACKUP_KEY);
}

export function restoreAdminTokens() {
  const access = localStorage.getItem(ADMIN_ACCESS_BACKUP_KEY);
  const refresh = localStorage.getItem(ADMIN_REFRESH_BACKUP_KEY);
  if (!access || !refresh) return false;
  setTokens(access, refresh);
  localStorage.removeItem(ADMIN_ACCESS_BACKUP_KEY);
  localStorage.removeItem(ADMIN_REFRESH_BACKUP_KEY);
  return true;
}

export async function tryRefresh() {
  const refresh = getRefreshToken();
  if (!refresh) return REFRESH_FAILED;
  try {
    const res = await fetch(`${BASE}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) {
      if ((res.status === 401 || res.status === 403) && restoreAdminTokens()) {
        return IDENTITY_RESTORED;
      }
      return REFRESH_FAILED;
    }
    const data = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return REFRESHED;
  } catch {
    return REFRESH_FAILED;
  }
}

export async function login(email, password) {
  const res = await fetch(`${BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    const error = new Error(data.detail || 'Login failed');
    error.status = res.status;
    throw error;
  }
  clearTokens();
  setTokens(data.access_token, data.refresh_token);
  return data;
}

export async function googleLogin(credential) {
  const res = await fetch(`${BASE}/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Google sign-in failed');
  clearTokens();
  setTokens(data.access_token, data.refresh_token);
  return data;
}

export async function register(email, password, displayName) {
  const res = await fetch(`${BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, display_name: displayName || undefined }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Registration failed');
  return data;
}

export async function resendVerification(email) {
  const res = await fetch(`${BASE}/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Could not resend verification email');
  return data;
}

export async function forgotPassword(email) {
  const res = await fetch(`${BASE}/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
}

export async function updateProfile(fields) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}/me`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(fields),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Update failed');
  return data;
}

export async function getMe() {
  const token = getAccessToken();
  try {
    const res = await fetch(`${BASE}/me`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function changePassword(currentPassword, newPassword) {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${BASE}/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Password change failed');
  return data;
}

export function logout() {
  clearTokens();
}

export async function startMasquerade(targetUserId) {
  const access = getAccessToken();
  const refresh = getRefreshToken();
  if (!access || !refresh) throw new Error('Not authenticated');
  if (hasAdminTokenBackup()) throw new Error('A masquerade session is already active');

  const res = await fetch('/api/admin/masquerade', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${access}` },
    body: JSON.stringify({ target_user_id: targetUserId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Could not start masquerading');

  localStorage.setItem(ADMIN_ACCESS_BACKUP_KEY, access);
  localStorage.setItem(ADMIN_REFRESH_BACKUP_KEY, refresh);
  setTokens(data.access_token, data.refresh_token);
  return data;
}

export async function stopMasquerade() {
  if (!getAccessToken() || !hasAdminTokenBackup()) throw new Error('No masquerade session is active');

  const requestStop = async () => {
    try {
      return await fetch('/api/admin/masquerade/stop', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
    } catch {
      throw new Error('Could not contact the server to revoke masquerade access');
    }
  };

  let res = await requestStop();
  if (res.status === 401) {
    const refreshResult = await tryRefresh();
    if (refreshResult === IDENTITY_RESTORED) return;
    if (refreshResult === REFRESHED) res = await requestStop();
    else throw new Error('Could not refresh the masquerade session for revocation');
  }

  // An expired/deleted target has already made the masquerade token unusable.
  if (!res.ok && ![400, 401, 404].includes(res.status)) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Could not stop masquerading');
  }
  if (!restoreAdminTokens()) throw new Error('Original admin session is unavailable');
}

// ── API Keys ──────────────────────────────────────────────────────────────

export async function listApiKeys() {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${BASE}/api-keys`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Failed to load API keys');
  return data;
}

export async function createApiKey(label) {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${BASE}/api-keys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ label }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Failed to create API key');
  return data;
}

export async function revokeApiKey(keyId) {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${BASE}/api-keys/${keyId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to revoke API key');
  }
}

export function getCurrentUserId() {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || null;
  } catch {
    return null;
  }
}
